/**
 * CTA リンク フィールド。
 *
 * 「フォーム選択」/「外部 URL」の 2 モード切替で入力。
 *  - フォーム: 既存フォームから select し、`/public/forms/{formId}` として保存
 *  - 外部 URL: 任意 URL（mailto: tel: 等含む）を text 入力
 *
 * 保存後は ctaFallback の enrichContentWithFallbackCta が `/public/forms/...` を含むリンクに
 * `?campaignId=` / `?cid=` / `?lid=` / `?src=landing` を自動付与（公開時 / プレビュー時とも）。
 *
 * 既存 string データとの後方互換:
 *  - `/public/forms/{formId}` で始まる → 「フォーム」モードを初期選択
 *  - それ以外 → 「外部 URL」モードを初期選択
 *  - 空 / "#" → 「フォーム」モードで未選択
 */
import { ExternalLink, FileText, Link2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Input, Label } from '@/components/ui/input';
import { useForms } from '@/hooks/useForms';

type Mode = 'form' | 'url';

interface Props {
  label: string;
  value: string;
  onChange: (next: string) => void;
  /** 「未選択」も許容するか（CTA がオプションなら true） */
  allowEmpty?: boolean;
}

const FORM_URL_RE = /^\/public\/forms\/([^/?#]+)/;

function detectFormId(v: string): string | null {
  const m = v.match(FORM_URL_RE);
  return m ? m[1] : null;
}

function detectInitialMode(v: string): Mode {
  if (!v || v === '#') return 'form';
  return detectFormId(v) ? 'form' : 'url';
}

export function CtaHrefField({ label, value, onChange, allowEmpty = true }: Props) {
  const { data: forms } = useForms();
  const [mode, setMode] = useState<Mode>(() => detectInitialMode(value));
  const [urlDraft, setUrlDraft] = useState<string>(value && !detectFormId(value) ? value : '');

  // 外部値の変化に追随（別経路で更新された場合）
  useEffect(() => {
    setMode(detectInitialMode(value));
    if (value && !detectFormId(value)) setUrlDraft(value);
  }, [value]);

  const selectedFormId = useMemo(() => detectFormId(value), [value]);

  const handleSelectForm = (id: string) => {
    if (!id) {
      if (allowEmpty) onChange('');
      return;
    }
    onChange(`/public/forms/${id}`);
  };

  const handleUrlCommit = (next: string) => {
    setUrlDraft(next);
    onChange(next.trim());
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-[11px]">{label}</Label>

      {/* モードトグル */}
      <div className="inline-flex border border-[var(--border)] rounded-md overflow-hidden text-[11px]">
        <button
          type="button"
          onClick={() => setMode('form')}
          className={`px-2.5 py-1 flex items-center gap-1 transition-colors ${
            mode === 'form'
              ? 'bg-[var(--accent)] text-white'
              : 'bg-transparent hover:bg-[var(--bg-hover)]'
          }`}
        >
          <FileText className="size-3" /> フォーム
        </button>
        <button
          type="button"
          onClick={() => setMode('url')}
          className={`px-2.5 py-1 flex items-center gap-1 transition-colors ${
            mode === 'url'
              ? 'bg-[var(--accent)] text-white'
              : 'bg-transparent hover:bg-[var(--bg-hover)]'
          }`}
        >
          <Link2 className="size-3" /> 外部 URL
        </button>
      </div>

      {/* フォーム選択モード */}
      {mode === 'form' && (
        <div className="space-y-1">
          <select
            value={selectedFormId ?? ''}
            onChange={(e) => handleSelectForm(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-[var(--bg-input)] border border-[var(--border)] rounded-sm"
          >
            <option value="">{allowEmpty ? '(未選択)' : 'フォームを選んでください'}</option>
            {(forms ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.name || '(無題)'}
              </option>
            ))}
          </select>
          {selectedFormId && (
            <div className="flex items-center gap-2 text-[10px] text-[var(--text-muted)]">
              <code className="font-mono">/public/forms/{selectedFormId}</code>
              <a
                href={`/public/forms/${selectedFormId}`}
                target="_blank"
                rel="noreferrer"
                className="text-[var(--accent)] hover:underline inline-flex items-center gap-0.5"
                title="フォームを別タブで開く"
              >
                <ExternalLink className="size-2.5" />
              </a>
            </div>
          )}
          <p className="text-[10px] text-[var(--text-muted)]">
            公開時は <code>?campaignId=</code> / <code>?cid=</code> / <code>?src=landing</code> が自動付与されます。
          </p>
        </div>
      )}

      {/* 外部 URL モード */}
      {mode === 'url' && (
        <div className="space-y-1">
          <Input
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            onBlur={() => handleUrlCommit(urlDraft)}
            placeholder="https://example.com / mailto:hello@example.com / tel:+81-3-..."
            className="font-mono text-xs"
          />
          <p className="text-[10px] text-[var(--text-muted)]">
            外部 URL は変更されずそのまま使われます (識別子の自動付与はなし)。
          </p>
        </div>
      )}
    </div>
  );
}
